import ConnectedContainer from "components/container/ConnectedContainer.tsx";
import { useAppDispatch, useAppSelector } from "config/store";
import { UserType } from "globals/types/models.ts";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon, { IconNameType } from "components/atom/Icon.tsx";
import { authActions } from "config/store/modules/auth.ts";
import ajax, { resetSessionId } from "config/ajax.ts";
import FileUploader from "components/form/FileUploader.tsx";
import { tryAsyncFunc } from "globals/functions/async.ts";
import LangAndPolicies from "components/layouts/LangAndPolicies.tsx";
import { useTranslation } from "react-i18next";
import { STORAGE_WELCOME_MODAL_KEY } from "utils/data/global.ts";
import { USER_TYPES } from "globals/constants.ts";
import { EnumUserTypeType } from "globals/types/globals.ts";
import ConfirmActionModal from "components/modals/ConfirmActionModal.tsx";

function MePage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currentUser: UserType | null = useAppSelector(state => state.auth.user);
  const currentUserIsCompleted: boolean = useAppSelector(state => state.auth.userIsCompleted);

  const [showConfirmSwitchTypeModal, setShowConfirmSwitchTypeModal] = useState<boolean>(false);

  const logout = useCallback(() => {
    dispatch(authActions.setJwt(null));
    dispatch(authActions.setUser(null));
    resetSessionId();
    window.localStorage.removeItem(STORAGE_WELCOME_MODAL_KEY);
    navigate({ pathname: "/map", hash: "#/auth/login" });
  }, [dispatch, navigate]);

  const goTo = useCallback((route: string) => {
    //@ts-ignore
    return (e: any) => {
      navigate(route);
    };
  }, [navigate]);

  const uploadImage = useCallback((file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "users");
    return ajax.post("/connected/globals/upload", formData);
  }, []);

  const handleMultipleAddFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    const [uploadImageRes, err] = await tryAsyncFunc(uploadImage(file));

    if (err) {
      if (window.Sentry) {
        window.Sentry.captureException(err);
      }
      // eslint-disable-next-line no-console
      console.error(err);
      return;
    }

    const [userRes, userErr] = await tryAsyncFunc(ajax.put("/connected/me", {
      imagePath: uploadImageRes.data.path
    }));
    if (userErr) {
      if (window.Sentry) {
        window.Sentry.captureException(userErr);
      }
      // eslint-disable-next-line no-console
      console.error(userErr);
      return;
    }

    await dispatch(authActions.setUser(userRes.data.user));

  }, [dispatch, uploadImage]);

  const switchToType = useCallback(async (type: EnumUserTypeType) => {
    const [userRes, userErr] = await tryAsyncFunc(ajax.put("/connected/me", {
      type: type
    }));
    if (userErr) {
      if (window.Sentry) {
        window.Sentry.captureException(userErr);
      }
      // eslint-disable-next-line no-console
      console.error(userErr);
      return;
    }

    await dispatch(authActions.setUser(userRes.data.user));
    navigate(`/profile`);//so the user is redirected to the profile page and can create events

  }, [dispatch, navigate]);

  if (!currentUser) {
    return null;
  }

  return (
    <ConnectedContainer title={t("menu.settings")}>
      <div className="mx-auto flex h-full w-full flex-col items-center justify-center gap-1 md:max-w-[600px]">
        <div
          className="min-w-16 h-16 w-16 relative group bg-gray-100 flex flex-col justify-center items-center overflow-hidden rounded-full">

          <FileUploader
            multiple={false}
            handleFiles={handleMultipleAddFiles}
          >
            <div className="relative group w-full h-full">
              {currentUser.imageUrl && (
                <img src={currentUser.imageUrl}
                     className="block w-full h-full object-cover object-center" />
              )}
              <div
                style={{ background: "rgba(0,0,0,0.3)" }}
                className="pointer-events-none absolute inset-0 flex h-full w-full flex-row items-center justify-center gap-2 opacity-0 transition duration-100 ease-in group-hover:pointer-events-auto group-hover:opacity-100">

                <button
                  className="pointer-events-none flex appearance-none flex-col items-center justify-center rounded-full bg-white p-1"
                  type="button">
                  <Icon name="upload" className="h-4 w-4 text-black" />
                </button>
              </div>
            </div>
          </FileUploader>
        </div>

        <h2 className="text-center pt-2 text-xl text-gray-900">
          {currentUser.name}
        </h2>
        <p className="text-center text-sm text-gray-900">
          {currentUser.email}
        </p>

        <div className="mt-6 flex w-full flex-col items-center justify-center gap-4">
          {currentUser.type === USER_TYPES.VIEWER && (
            <div
              className="flex w-full cursor-pointer flex-row items-center justify-between rounded-md bg-green-500 px-5 py-4 hover:shadow"
              onClick={() => setShowConfirmSwitchTypeModal(true)}
            >
              <div className="flex flex-col justify-start items-start gap-1">
                <h3 className="inline-flex flex-row items-center justify-start gap-2 text-base text-black font-medium">
                  {t("menu.switchToEventManager")}
                </h3>
                <p className="text-black text-xs">
                  {t("menu.switchToEventManagerDescription")}
                </p>
              </div>
              <button type="button" className="appearance-none">
                <Icon name="arrow-up-right" className="block h-6 w-6 min-w-6" />
              </button>
            </div>
          )}
          <RowItem onClick={goTo(`/profile/me/account`)}>
            {t("menu.account")}
            {!currentUserIsCompleted && (
              <Icon name="alert-circle" className="block h-6 w-6 text-red-500" />
            )}
          </RowItem>
          <RowItem onClick={goTo(`/profile/me/password`)}>
            {t("menu.changePwd")}
          </RowItem>
          <RowItem onClick={goTo(`/profile/me/contact`)}>
            {t("menu.contactUs")}
          </RowItem>
          <RowItem onClick={goTo(`/profile/me/deletion`)}>
            {t("menu.deleteMyAccount")}
          </RowItem>
          <RowItem onClick={logout} icon="arrow-turn-down-left">
            {t("menu.logout")}
          </RowItem>
          <LangAndPolicies showSocialLinks />
        </div>
      </div>
      <ConfirmActionModal
        isOpen={showConfirmSwitchTypeModal}
        onClose={() => setShowConfirmSwitchTypeModal(false)}
        onAccept={() => switchToType(USER_TYPES.EVENT_MANAGER)}
      >
        <p className="text-center text-gray-400">
          {t("user.confirmSwitchType")}
        </p>
      </ConfirmActionModal>
    </ConnectedContainer>
  );
}

function RowItem(props: { children: React.ReactNode, onClick: (e: any) => void, icon?: IconNameType }) {
  const {
    children,
    onClick,
    icon = "angle-right"
  } = props;
  return (
    <div className="flex w-full cursor-pointer flex-row items-center justify-between rounded-md bg-gray-50 px-5 py-4"
         onClick={onClick}
    >
      <h3 className="inline-flex flex-row items-center justify-start gap-2 text-base font-medium">
        {children}
      </h3>
      <button type="button" className="appearance-none">
        <Icon name={icon} className="block h-6 w-6 min-w-6" />
      </button>
    </div>
  );
}

export default MePage;
